// frontend/src/errors/errorMessages.ts
// Centralized error message mappings

import { ErrorCategory } from './errorTypes';

/**
 * User-friendly error messages by category
 */
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK: {
    CONNECTION_LOST: 'Connection lost. Please check your internet connection.',
    REQUEST_TIMEOUT: 'Request timed out. Please try again.',
    SERVER_UNREACHABLE: 'Unable to reach the server. Please try again later.',
    OFFLINE: 'You are offline. Please check your internet connection.',
  },

  // Authentication errors
  AUTH: {
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
    INVALID_CREDENTIALS: 'Invalid credentials. Please try again.',
  },

  // Not found errors
  NOT_FOUND: {
    RESOURCE: 'The requested resource was not found.',
    CLIENT: 'Client not found.',
    PET: 'Pet not found.',
    BOOKING: 'Booking not found.',
  },

  // Server errors
  SERVER: {
    INTERNAL: 'Something went wrong on our end. Please try again.',
    SERVICE_UNAVAILABLE:
      'Service temporarily unavailable. Please try again later.',
    DATABASE: 'Database error occurred. Please try again.',
  },

  // Validation errors
  VALIDATION: {
    GENERIC: 'Please check your input and try again.',
    REQUIRED_FIELDS: 'Please fill in all required fields.',
  },

  // Generic fallback
  GENERIC: {
    UNKNOWN: 'An unexpected error occurred. Please try again.',
    OPERATION_FAILED: 'Operation failed. Please try again.',
  },

  // Operation-specific errors
  OPERATIONS: {
    LOAD_FAILED: (resource: string) =>
      `Failed to load ${resource}. Please try again.`,
    SAVE_FAILED: (resource: string) =>
      `Failed to save ${resource}. Please try again.`,
    DELETE_FAILED: (resource: string) =>
      `Failed to delete ${resource}. Please try again.`,
    UPDATE_FAILED: (resource: string) =>
      `Failed to update ${resource}. Please try again.`,
    CREATE_FAILED: (resource: string) =>
      `Failed to create ${resource}. Please try again.`,
  },
};

/**
 * Map HTTP status codes to user-friendly messages
 */
export const getMessageForStatusCode = (
  statusCode: number,
  context?: string
): string => {
  // const contextName = context || 'data';

  switch (statusCode) {
    case 400:
      return ERROR_MESSAGES.VALIDATION.GENERIC;
    case 401:
      return ERROR_MESSAGES.AUTH.UNAUTHORIZED;
    case 403:
      return ERROR_MESSAGES.AUTH.UNAUTHORIZED;
    case 404:
      return ERROR_MESSAGES.NOT_FOUND.RESOURCE;
    case 408:
      return ERROR_MESSAGES.NETWORK.REQUEST_TIMEOUT;
    case 500:
      return ERROR_MESSAGES.SERVER.INTERNAL;
    case 503:
      return ERROR_MESSAGES.SERVER.SERVICE_UNAVAILABLE;
    default:
      return ERROR_MESSAGES.GENERIC.UNKNOWN;
  }
};

/**
 * Get error category from status code
 */
export const getCategoryFromStatusCode = (
  statusCode: number
): ErrorCategory => {
  if (statusCode >= 500) return ErrorCategory.SERVER;
  if (statusCode === 404) return ErrorCategory.NOT_FOUND;
  if (statusCode === 401 || statusCode === 403)
    return ErrorCategory.AUTHENTICATION;
  if (statusCode === 400 || statusCode === 422) return ErrorCategory.VALIDATION;
  if (statusCode >= 400) return ErrorCategory.CLIENT;
  return ErrorCategory.UNKNOWN;
};

/**
 * Check if error is retryable based on status code
 */
export const isRetryableError = (statusCode?: number): boolean => {
  if (!statusCode) return true;
  // Network errors, server errors, and timeouts are retryable
  // Auth and validation errors are not
  return statusCode >= 500 || statusCode === 408 || statusCode === 0;
};
